import React, {useEffect, useMemo} from "react";
import {useAppDispatch, useAppSelector} from "../../../../../app/hooks";
import {CreateOfferRequest} from "../../../../../api/generated";
import {
    clearSaveNewOffersModelSlice,
    existOffersListRequestStatusSelector,
    existOffersListSelector,
    fetchExistOffersList,
    saveNewOffers,
    saveNewOffersRequestStatusSelector
} from "./saveNewOffersModalSlice";
import styles from "./SaveNewOffersConfirmationModal.module.css";
import {CloseImageButton} from "../../../closeImageButton/CloseImageButton";
import {Button, ButtonState} from "../../../button/Button";
import {showSuccessToastNotification} from "../../../toastNotification/toastNotificationSlice";
import {RequestStatus} from "../../../../../api/apiConfiguration";
import {OfferRow} from "./row/OfferRow";
import {TableLoader} from "../../../tableLoader/TableLoader";
import {
    fetchOfferList,
    fetchOfferMakeOptions, fetchOfferModelOptions, fetchOfferSourceNameOptions,
    offerTableRequestSelector
} from "../../../../modules/offer/offerListSlice";

interface ConfirmationModalProps {
    publicBotId?: string
    publicLocationId?: string
    offersToSave: CreateOfferRequest[]
    offerSourceName: string
    handleClose: () => void
}

export const SaveNewOffersConfirmationModal: React.FC<ConfirmationModalProps> = ({
                                                                                     publicBotId,
                                                                                     publicLocationId,
                                                                                     offersToSave,
                                                                                     offerSourceName,
                                                                                     handleClose,
                                                                                 }) => {
    const dispatch = useAppDispatch();

    const existOffersList = useAppSelector(existOffersListSelector);
    const offerTableRequest = useAppSelector(offerTableRequestSelector);
    const existOffersListRequestStatus = useAppSelector(existOffersListRequestStatusSelector);
    const saveNewOffersRequestStatus = useAppSelector(saveNewOffersRequestStatusSelector);

    useEffect(() => {
        dispatch(fetchExistOffersList({
            offerSourceNameList: [offerSourceName],
            pageNumber: 0,
            pageSize: 100000,
            publicBotId,
            publicLocationId
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerSourceName]);

    useEffect(() => {
        if (saveNewOffersRequestStatus === RequestStatus.RESOLVED) {
            dispatch(clearSaveNewOffersModelSlice())
            dispatch(fetchOfferList({...offerTableRequest, publicBotId, publicLocationId}))
            dispatch(fetchOfferMakeOptions({publicBotId, publicLocationId}));
            dispatch(fetchOfferModelOptions({publicBotId, publicLocationId}));
            dispatch(fetchOfferSourceNameOptions({publicBotId, publicLocationId}));
            dispatch(showSuccessToastNotification({message: "Offers from CSV file successfully saved"}));
            handleClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveNewOffersRequestStatus]);

    const onConfirm = () => dispatch(saveNewOffers(offersToSave));

    const offersToDelete = useMemo(() => existOffersList ?? [], [existOffersList]);

    const hasOld = offersToDelete.length > 0;
    const hasNew = offersToSave.length > 0;

    if (existOffersListRequestStatus !== RequestStatus.RESOLVED) {
        return <div className={styles.wrapper}>
            <TableLoader/>
        </div>
    }

    return <div className={styles.wrapper}>
        <div className={styles.textWrapper}>
            <div className={styles.titleWrapper}>
                <span className={styles.title}>Confirm CSV Import</span>
                <CloseImageButton onClose={handleClose}/>
            </div>
            <span className={styles.subtitle}>
                    {hasOld && hasNew && "Existing offers will be replaced by new ones from the CSV file."}
                {!hasOld && hasNew && "These new offers will be added. No existing offers will be removed."}
                {hasOld && !hasNew && "All existing offers from this source will be removed."}
                </span>
        </div>

        <div className={styles.summaryBar}>
            {hasOld && (
                <div className={styles.summaryItemDelete}>
                    {offersToDelete.length} will be removed
                </div>
            )}

            {hasOld && hasNew && <div className={styles.summaryArrow}>→</div>}

            {hasNew && (
                <div className={styles.summaryItemAdd}>
                    {offersToSave.length} will be added
                </div>
            )}
        </div>


        <div
            className={`${styles.tablesWrapper} ${
                !hasOld || !hasNew ? styles.singleTable : ""
            }`}
        >
            {hasOld && (
                <div className={styles.tableBlock}>
                    <div className={styles.tableTitle}>Existing Offers</div>
                    <div className={styles.table}>
                        {offersToDelete.map(offer => (
                            <OfferRow
                                isNew={false}
                                title={offer.title}
                                description={offer.description}
                                make={offer.make}
                                model={offer.model}
                                startDate={offer.startDate}
                                endDate={offer.endDate}
                                offerType={offer.offerType}
                                discount={offer.discount && {
                                    value: offer.discount!.subtractionValue!,
                                    type: offer.discount!.subtractionType!
                                }}
                                monthlyPayments={offer.monthlyPayments}
                            />
                        ))}
                    </div>
                </div>
            )}

            {hasNew && (
                <div className={styles.tableBlock}>
                    <div className={styles.tableTitle}>New Offers from CSV</div>
                    <div className={styles.table}>
                        {offersToSave.map((offer, i) => (
                            <OfferRow
                                isNew={true}
                                title={offer.title}
                                description={offer.description}
                                make={offer.make}
                                model={offer.model}
                                startDate={offer.startDate}
                                endDate={offer.endDate}
                                offerType={offer.offerType}
                                discount={offer.discount && {
                                    value: offer.discount.subtractionValue,
                                    type: offer.discount.subtractionType
                                }}
                                monthlyPayments={offer.monthlyPayments}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>


        <div className={styles.buttonsWrapper}>
            <Button
                title={"Cancel"}
                state={ButtonState.SECONDARY}
                handleClick={handleClose}
            />
            <Button
                title={saveNewOffersRequestStatus === RequestStatus.PENDING ? "Replacing..." : "Confirm & Replace"}
                state={ButtonState.PRIMARY}
                handleClick={onConfirm}
            />
        </div>
    </div>
}
